import { useWorldPollingCount } from "./useWorldPollingCount";

import { useWorldGateInstancesAwaiting } from "./gateInstancesClient";

import { type ConversationSummary, listConversations } from "./commsClient";

/**
 * The pinned Inbox tab's badge (2026-09-03, the principal: "a permanent
 * tab for the Inbox, in the top row … only wide enough to show the icon
 * and relevant state (new messages)"). The count is a FOLD over the CM4
 * conversation list's CM5 unreadCount projections — no second unread
 * authority, nothing stored (DL3/DL4). It refreshes on a slow cadence, on
 * window focus, and the moment the Inbox surface advances a read cursor
 * (notifyInboxReadStateChanged), so the badge never outlives the read.
 */
export const INBOX_UNREAD_POLL_MS = 30_000;

/** Sum of the list's unread projections; absent projections count as 0. */
export function sumInboxUnread(conversations: ReadonlyArray<ConversationSummary>): number {
  return conversations.reduce((sum, row) => sum + (row.unreadCount ?? 0), 0);
}

const readStateListeners = new Set<() => void>();

/** The Inbox surface calls this after a read cursor advances; every badge refetches. */
export function notifyInboxReadStateChanged(): void {
  for (const listener of readStateListeners) listener();
}

/**
 * Per-rail unread for the pinned tab's rail doors (2026-09-03). Only the
 * World rail's lane projects read state today (CM5 unreadCount); the Gmail
 * and iMessage lanes serve no unread projection yet, so their counts are an
 * honest ZERO — a named absence, never an invented number — until those
 * doors grow one (Gmail's UNREAD label, chat.db's is_read).
 */
export interface InboxRailUnread {
  readonly hitl: number;
  readonly world: number;
  readonly gmail: number;
  readonly imessage: number;
}

export const NO_RAIL_UNREAD: InboxRailUnread = { hitl: 0, world: 0, gmail: 0, imessage: 0 };

export function useWorldInboxRailUnread(worldKey: string | null): InboxRailUnread {
  const world = useWorldInboxUnread(worldKey);
  const hitl = useWorldGateInstancesAwaiting(worldKey);
  return world === 0 && hitl === 0 ? NO_RAIL_UNREAD : { hitl, world, gmail: 0, imessage: 0 };
}

async function readInboxCount(worldKey: string): Promise<number> {
  const outcome = await listConversations(worldKey);
  return outcome.ok ? sumInboxUnread(outcome.conversations) : 0;
}

export function useWorldInboxUnread(worldKey: string | null): number {
  return useWorldPollingCount(worldKey, readInboxCount, readStateListeners, INBOX_UNREAD_POLL_MS);
}
